<template>
  <div class="wcc">
    <!-- Toolbar: search, type filter pills, selection summary + bulk actions -->
    <div class="wcc__toolbar">
      <v-text-field
        v-model="query"
        class="wcc__search"
        placeholder="Search columns"
        aria-label="Search columns"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable>
      </v-text-field>

      <div class="wcc__pills" role="group" aria-label="Filter columns by type">
        <button
          v-for="pill in typePills"
          :key="pill.key"
          type="button"
          class="wcc__pill"
          :aria-pressed="String(typeFilter === pill.key)"
          @click="typeFilter = pill.key">
          {{ pill.label }} <span class="wcc__pill-n">{{ pill.n }}</span>
        </button>
      </div>

      <div class="wcc__actions">
        <span class="wcc__count" aria-live="polite"><b>{{ value.length }}</b>/{{ columns.length }}</span>
        <button type="button" class="wcc__link" @click="selectVisible">All</button>
        <button type="button" class="wcc__link" @click="clearVisible">None</button>
      </div>
    </div>

    <!-- Scrollable column list -->
    <div class="wcc__scroll">
      <div
        v-for="col in visibleColumns"
        :key="col.name"
        class="wcc__row"
        :class="{ 'wcc__row--off': !selectedSet.has(col.name) }"
        @click="toggle(col.name)">
        <input
          type="checkbox"
          class="wcc__cb"
          :checked="selectedSet.has(col.name)"
          :aria-label="`Include ${col.displayName || col.name}`"
          @click.stop="toggle(col.name)">
        <div class="wcc__text">
          <div class="wcc__name">{{ col.displayName || col.name }}</div>
          <div v-if="sampleText(col)" class="wcc__sample" :title="sampleText(col)">{{ sampleText(col) }}</div>
        </div>
        <span class="wcc__type">
          <v-icon size="13">{{ friendly(col.dataType).icon }}</v-icon>
          {{ friendly(col.dataType).label }}
        </span>
      </div>

      <div v-if="!visibleColumns.length" class="wcc__empty">
        No columns match "<b>{{ query }}</b>".
        <button type="button" class="wcc__link" @click="query = ''">Clear search</button>
      </div>
    </div>

    <!-- Footer: selected type mix + filtered-out note -->
    <div class="wcc__foot">
      <span>{{ mixSummary }}</span>
      <span class="wcc__hidden">{{ hiddenNote }}</span>
    </div>
  </div>
</template>

<script>
/**
 * WidgetColumnChooser — column selection list for the Create Widget wizard.
 *
 * Replaces the raw v-data-table picker: plain-language types instead of SQL
 * type names, search + type filtering, a sample value per column (harvested
 * by the parent from the preview fetch), and a selected-type-mix footer so
 * chart readiness (numbers/dates available) is visible at a glance.
 *
 * v-model is the array of selected column NAMES; the parent derives
 * columnTypes / columnDisplayNames from it (see EditSqlViewWidget).
 */
import { FRIENDLY_TYPES, FRIENDLY_TYPE_ORDER as TYPE_ORDER, friendlyType } from '../utils/columnTypes';

export default {
  name: 'WidgetColumnChooser',

  props: {
    /** Column descriptors from the columns endpoint: { name, displayName?, dataType, nullable } */
    columns: { type: Array, default: () => [] },
    /** v-model: selected column names */
    value: { type: Array, default: () => [] },
    /** Map of column name → sample value (parent harvests from the preview fetch) */
    samples: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      query: '',
      typeFilter: 'all',
    };
  },

  computed: {
    selectedSet() {
      return new Set(this.value);
    },

    /** All + only the types that exist in this source (a "Yes/No 0" pill is noise). */
    typePills() {
      const pills = [{ key: 'all', label: 'All', n: this.columns.length }];
      TYPE_ORDER.forEach(key => {
        const n = this.columns.filter(c => this.friendly(c.dataType).key === key).length;
        if (n > 0) pills.push({ key, label: FRIENDLY_TYPES[key].label, n });
      });
      return pills;
    },

    visibleColumns() {
      const needle = this.query ? this.query.trim().toLowerCase() : '';
      return this.columns.filter(c =>
        (this.typeFilter === 'all' || this.friendly(c.dataType).key === this.typeFilter) &&
        (!needle || (c.displayName || c.name).toLowerCase().includes(needle) || c.name.toLowerCase().includes(needle))
      );
    },

    /** "Selected: 18 text · 1 number · 2 date" — chart readiness at a glance. */
    mixSummary() {
      if (!this.value.length) return 'Nothing selected';
      const parts = TYPE_ORDER
        .map(key => [key, this.columns.filter(c => this.selectedSet.has(c.name) && this.friendly(c.dataType).key === key).length])
        .filter(([, n]) => n > 0)
        .map(([key, n]) => `${n} ${FRIENDLY_TYPES[key].label.toLowerCase()}`);
      return `Selected: ${parts.join(' · ')}`;
    },

    hiddenNote() {
      const hidden = this.columns.length - this.visibleColumns.length;
      if (hidden <= 0) return '';
      return `${hidden} hidden by ${this.query ? 'search' : 'filter'}`;
    },
  },

  methods: {
    friendly(dataType) {
      return friendlyType(dataType);
    },

    sampleText(col) {
      const v = this.samples ? this.samples[col.name] : undefined;
      if (v === undefined || v === null || v === '') return '';
      if (this.friendly(col.dataType).key === 'bool') {
        return (v === true || v === 'true' || v === 1) ? 'Yes' : 'No';
      }
      if (typeof v === 'object') {
        try { return JSON.stringify(v); } catch (err) { return String(v); }
      }
      return String(v);
    },

    toggle(name) {
      const next = this.selectedSet.has(name)
        ? this.value.filter(n => n !== name)
        : [...this.value, name];
      this.$emit('input', next);
    },

    /** Bulk actions operate on the VISIBLE set so they compose with search/filter. */
    selectVisible() {
      const next = new Set(this.value);
      this.visibleColumns.forEach(c => next.add(c.name));
      this.$emit('input', [...next]);
    },

    clearVisible() {
      const visible = new Set(this.visibleColumns.map(c => c.name));
      this.$emit('input', this.value.filter(n => !visible.has(n)));
    },
  },
};
</script>

<style lang="scss" scoped>
.wcc {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
  border-radius: 8px;
  background: var(--ds-surface-elevated, #fcfdfc);
  overflow: hidden;
  letter-spacing: 0;
}

/* ---------- toolbar ---------- */
.wcc__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
  flex: 0 0 auto;
  flex-wrap: wrap;
}

.wcc__search {
  flex: 0 1 220px;
  min-width: 150px;
}

.wcc__pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.wcc__pill {
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0;
  color: var(--ds-text-secondary, #55605c);
  background: none;
  border: 1px solid var(--ds-border-strong, #c9cfcc);
  border-radius: 999px;
  height: 28px;
  padding: 0 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.wcc__pill:hover {
  border-color: var(--ds-text-secondary, #55605c);
  color: var(--ds-text-primary, #1d2321);
}

.wcc__pill[aria-pressed='true'] {
  background: var(--v-primary-base, #03624c);
  border-color: var(--v-primary-base, #03624c);
  color: #fff;
}

.wcc__pill:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(3, 98, 76, 0.32);
}

.wcc__pill-n {
  opacity: 0.65;
  font-weight: 400;
}

.wcc__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.wcc__count {
  font-size: 12.5px;
  color: var(--ds-text-secondary, #55605c);

  b {
    color: var(--ds-text-primary, #1d2321);
    font-weight: 600;
  }
}

.wcc__link {
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--v-primary-base, #03624c);
  background: none;
  border: 0;
  padding: 4px 2px;
  cursor: pointer;
  border-radius: 4px;
}

.wcc__link:hover {
  text-decoration: underline;
}

.wcc__link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(3, 98, 76, 0.32);
}

/* ---------- rows ---------- */
.wcc__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.wcc__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 14px;
  border-bottom: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
  cursor: pointer;
}

.wcc__row:last-child {
  border-bottom: 0;
}

.wcc__row:hover {
  background: var(--ds-surface-hover, #f4f7f6);
}

.wcc__text {
  min-width: 0;
  flex: 1;
}

.wcc__name {
  font-weight: 500;
  font-size: 13.5px;
  color: var(--ds-text-primary, #1d2321);
}

.wcc__row--off .wcc__name {
  color: var(--ds-text-disabled, #8a938f);
  font-weight: 400;
}

.wcc__sample {
  font-family: ui-monospace, Consolas, 'IBM Plex Mono', monospace;
  font-size: 11.5px;
  color: var(--ds-text-disabled, #8a938f);
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wcc__type {
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--ds-text-secondary, #55605c);
  white-space: nowrap;

  .v-icon {
    color: var(--ds-text-disabled, #8a938f);
  }
}

/* ---------- checkbox (green when checked, real input for a11y) ---------- */
.wcc__cb {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border: 1.5px solid var(--ds-border-strong, #c9cfcc);
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  position: relative;
  margin: 0;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.wcc__cb:hover {
  border-color: var(--ds-text-disabled, #8a938f);
}

.wcc__cb:checked {
  background: var(--v-primary-base, #03624c);
  border-color: var(--v-primary-base, #03624c);
}

.wcc__cb:checked::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 1.5px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(42deg);
}

.wcc__cb:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(3, 98, 76, 0.32);
}

/* ---------- empty + footer ---------- */
.wcc__empty {
  padding: 42px 20px;
  text-align: center;
  color: var(--ds-text-secondary, #55605c);
}

.wcc__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-top: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
  font-size: 12.5px;
  color: var(--ds-text-secondary, #55605c);
  flex: 0 0 auto;
}

.wcc__hidden {
  margin-left: auto;
}

/* ---------- dark theme ---------- */
.theme--dark .wcc {
  background: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .wcc__toolbar,
.theme--dark .wcc__foot,
.theme--dark .wcc__row {
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .wcc__row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.theme--dark .wcc__name {
  color: rgba(255, 255, 255, 0.9);
}

.theme--dark .wcc__cb {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.4);
}

.theme--dark .wcc__cb:checked {
  background: var(--v-primary-base, #03624c);
  border-color: var(--v-primary-base, #03624c);
}
</style>
